import { useState } from "react";
import { Linkedin, UserRound } from "lucide-react";

const FOUNDERS = [
  { name: "Prinkan Pal", role: "Co-founder & CEO", initials: "PP", linkedin: "#", bio: "An AI and analytics leader with a proven track record of delivering high-impact enterprise solutions. At Intugle, Prinkan specializes in building and scaling data-driven products that transform complex business challenges into scalable, contextually intelligent AI applications." },
  { name: "Manan Pachnanda", role: "Co-founder & CPO", initials: "MP", linkedin: "#", bio: "A strategy and AI leader with over 12 years of experience driving business transformation for Fortune 500 organizations. At Intugle, Manan works at the intersection of enterprise strategy, data engineering, semantic intelligence, and agentic AI to deliver scalable business outcomes." },
  { name: "Jaskaran Singh", role: "Co-founder & CTO", initials: "JS", linkedin: "#", bio: "An AI and data science leader with extensive experience in machine learning, natural language processing, and enterprise intelligence. At Intugle, Jaskaran focuses on building context-aware AI systems that help organizations transform complex enterprise data into actionable knowledge and scalable AI solutions." },
];

const Ph = ({ className = "", label = true }) => (
  <div className={`relative bg-[#2A1A0D] ring-1 ring-white/10 flex flex-col items-center justify-center text-white/25 overflow-hidden ${className}`}>
    <UserRound size={44} strokeWidth={1} />
    {label && <span className="mt-2 font-mono2 text-[10px] uppercase tracking-[0.24em]">Photo</span>}
  </div>
);

const Label = ({ children }) => (
  <div className="bg-black text-[#EDA13A] font-mono2 text-xs uppercase tracking-[0.28em] px-6 py-3">{children}</div>
);

const Head = () => (
  <>
    <p className="font-mono2 text-[11px] uppercase tracking-[0.28em] mb-4 text-[#EDA13A]">Leadership</p>
    <h2 className="font-display font-medium tracking-tight text-2xl sm:text-3xl lg:text-[2.5rem] leading-[1.12] max-w-4xl">Meet the Founders</h2>
  </>
);

// ---------- E1: Spotlight (interactive left rail) ----------
export const LeadE1 = () => {
  const [active, setActive] = useState(0);
  const f = FOUNDERS[active];
  return (
    <section className="bg-[#211307] text-[#F7F5F0] py-20 lg:py-28" data-testid="lead-e1">
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <Head />
        <div className="mt-12 grid lg:grid-cols-[300px_1fr] gap-8 lg:gap-14">
          <div className="flex flex-col gap-3">
            {FOUNDERS.map((x, i) => (
              <button key={x.name} onClick={() => setActive(i)} className={`text-left flex items-center gap-4 rounded-xl p-3 transition-colors duration-300 ${i === active ? "bg-white/[0.06] ring-1 ring-[#EDA13A]/40" : "hover:bg-white/[0.03]"}`}>
                <Ph className="w-14 h-14 rounded-lg shrink-0" label={false} />
                <div>
                  <div className={`font-display text-lg ${i === active ? "text-[#EDA13A]" : "text-white"}`}>{x.name}</div>
                  <div className="font-mono2 text-[10px] uppercase tracking-[0.16em] text-white/50">{x.role}</div>
                </div>
              </button>
            ))}
          </div>
          <div className="grid sm:grid-cols-[300px_1fr] gap-8 items-start">
            <Ph className="w-full aspect-[4/5] rounded-2xl" />
            <div>
              <h3 className="font-display text-3xl font-medium">{f.name}</h3>
              <p className="mt-2 font-mono2 text-xs uppercase tracking-[0.18em] text-[#EDA13A]">{f.role}</p>
              <p className="mt-5 text-base text-white/70 leading-relaxed">{f.bio}</p>
              <a href={f.linkedin} className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-white/80 hover:text-[#EDA13A] transition-colors"><Linkedin size={16} /> Connect on LinkedIn</a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ---------- E2: Staggered gallery ----------
export const LeadE2 = () => (
  <section className="bg-[#211307] text-[#F7F5F0] py-20 lg:py-28" data-testid="lead-e2">
    <div className="max-w-6xl mx-auto px-6 sm:px-8">
      <Head />
      <div className="mt-14 grid md:grid-cols-3 gap-8 items-start">
        {FOUNDERS.map((f, i) => (
          <div key={f.name} className={`${i === 1 ? "md:mt-16" : ""}`}>
            <Ph className="w-full aspect-[3/4] rounded-2xl" />
            <h3 className="mt-6 font-display text-2xl font-medium">{f.name}</h3>
            <p className="mt-1 font-mono2 text-[11px] uppercase tracking-[0.16em] text-[#EDA13A]">{f.role}</p>
            <p className="mt-4 text-[13px] text-white/60 leading-relaxed">{f.bio}</p>
            <a href={f.linkedin} className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-white/80 hover:text-[#EDA13A] transition-colors"><Linkedin size={16} /> Connect</a>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ---------- E3: Big index rows ----------
export const LeadE3 = () => (
  <section className="bg-[#211307] text-[#F7F5F0] py-20 lg:py-28" data-testid="lead-e3">
    <div className="max-w-6xl mx-auto px-6 sm:px-8">
      <Head />
      <div className="mt-10">
        {FOUNDERS.map((f, i) => (
          <div key={f.name} className="grid md:grid-cols-[80px_180px_1fr_auto] gap-6 md:gap-10 items-center py-10 border-t border-white/10">
            <span className="font-display text-5xl font-medium text-white/12 leading-none">{String(i + 1).padStart(2, "0")}</span>
            <Ph className="w-36 h-44 rounded-2xl" />
            <div>
              <h3 className="font-display text-2xl font-medium">{f.name}</h3>
              <p className="mt-1 font-mono2 text-[11px] uppercase tracking-[0.16em] text-[#EDA13A]">{f.role}</p>
              <p className="mt-4 text-sm text-white/65 leading-relaxed max-w-2xl">{f.bio}</p>
            </div>
            <a href={f.linkedin} className="shrink-0 w-11 h-11 rounded-full ring-1 ring-white/20 flex items-center justify-center text-white/70 hover:bg-[#EDA13A] hover:text-[#211307] hover:ring-[#EDA13A] transition-colors"><Linkedin size={18} /></a>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export default function LeadershipDark2() {
  return (
    <div>
      <Label>Option E1 — Spotlight (click a founder on the left)</Label>
      <LeadE1 />
      <Label>Option E2 — Staggered Gallery</Label>
      <LeadE2 />
      <Label>Option E3 — Big Index Rows</Label>
      <LeadE3 />
    </div>
  );
}
